/**
 * PoolCustodyPanel — « le parc, maintenant » : ce qui est DEHORS.
 *
 * Le complément de la file d'écarts, et non son remplaçant. Les écarts montrent
 * ce qui a DÉJÀ dérapé ; ce panneau montre ce qui va déraper — qui détient quoi,
 * jusqu'à quand, et ce qui devait rentrer et n'est pas rentré.
 *
 * C'est la hiérarchie de la variante « ligne de temps » des maquettes retenues
 * (`docs/maquettes-parc/BRIEF-IMPLEMENTATION.md`) : état du parc → détentions et
 * retours proches → écarts. Pas un second module métier : les mêmes données, la
 * même permission, au-dessus de la même file.
 *
 * ⚠️ LES NOMBRES NE SE COMPTENT PAS DEUX FOIS. « En retard » est un
 * sous-ensemble de « dehors », et l'écran le DIT au lieu de laisser le lecteur
 * additionner. Un tableau de bord dont les chiffres se recouvrent en silence
 * devient faux six mois plus tard sans que personne s'en aperçoive.
 *
 * ⚠️ LE RETARD SE LIT EN DURÉE, jamais en horodatage brut : « 22 h 40 de
 * retard », pas « attendu le 13/09 à 18:00 ». Même règle que l'entrée de menu.
 *
 * La clôture d'office est une action SENSIBLE sur un actif d'une collectivité :
 * motif obligatoire (imposé par le serveur, pas seulement par ce formulaire),
 * conséquence écrite en toutes lettres avant de confirmer, et journalisation
 * nominative annoncée à celui qui décide.
 *
 * ⚠️ ONGLET « Maintenant » de « Parc partagé », plus une carte à chevron :
 * `PoolExceptionsView.jsx` possède le filtre U.T. (`siteId`), la recherche
 * (`search`) et la liste des sites — ce composant n'est qu'un CONTENU d'onglet
 * (contrat de composition, refonte 2026-09-14).
 *
 * ⚠️ 900 véhicules / 1 000 conducteurs : on résume d'abord, on détaille
 * l'exception, on laisse le reste à un clic (principe UX Jean-Guy 2026-09-14).
 * D'où le bandeau par U.T. (resserrer d'un clic) et la section « Dans les
 * temps » repliée par défaut — le seul cas normal ne mérite pas l'écran entier.
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Car, Clock, AlertTriangle, ShieldOff, Ban, X, CheckCircle2, ChevronDown, ChevronRight, MapPin,
} from 'lucide-react';
import { poolCustodyAPI } from '../../api/pool.js';
import { useFormat } from '../../i18n/format.js';

/**
 * Vocabulaire FERMÉ des causes d'immobilisation (lot 1.2, audit CD31 §Q3) —
 * MÊMES codes que `BLOCK_CAUSES` côté serveur (`pool_service.mjs`), rien de
 * codé en dur hors ce vocabulaire. Les libellés voyagent en i18n
 * (`poolCustody.block.causes.*`) ; les CODES, eux, sont le contrat avec le
 * serveur et ne se traduisent jamais.
 */
const BLOCK_CAUSE_CODES = ['breakdown', 'accident', 'maintenance', 'inspection', 'other'];

/** `datetime-local` (heure du navigateur, sans décalage) → instant ISO avec décalage explicite. */
function localInputToInstant(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** L'inverse, pour préremplir un `datetime-local` avec « maintenant ». */
function instantToLocalInput(date) {
  const d = date instanceof Date ? date : new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Une durée en minutes, lisible : « 45 min », « 2 h 10 », « 22 h 40 », « 2 j 03 h ». */
export function formatDuration(min) {
  const m = Math.abs(Math.round(min ?? 0));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h < 24) return `${h} h ${String(r).padStart(2, '0')}`;
  const d = Math.floor(h / 24);
  return `${d} j ${String(h % 24).padStart(2, '0')} h`;
}

/**
 * Répartit les gardes en trois paniers, chacun trié pour son usage :
 * en retard (le plus en retard d'abord), rentrent dans l'heure (le plus
 * pressé d'abord), dans les temps (ordre stable, cette section n'est qu'un
 * filet de sécurité). PUR — aucune dépendance au DOM, testable seul.
 */
export function groupCustodies(custodies) {
  const late = [];
  const dueSoon = [];
  const onTime = [];
  for (const c of custodies || []) {
    if (c.late) {
      late.push(c);
    } else if ((c.remaining_min ?? Infinity) <= 60) {
      dueSoon.push(c);
    } else {
      onTime.push(c);
    }
  }
  late.sort((a, b) => (b.overdue_min ?? 0) - (a.overdue_min ?? 0));
  dueSoon.sort((a, b) => (a.remaining_min ?? 0) - (b.remaining_min ?? 0));
  return { late, dueSoon, onTime };
}

/**
 * Une puce par U.T. — le geste « je resserre sur mon site » d'un clic. Une
 * garde sans `site_id` rejoint « Sans U.T. » plutôt que de disparaître :
 * un total qui ne boucle pas avec la ligne de synthèse serait pire qu'un
 * groupe mal nommé. PUR — testable seul. Trié par nom, « Sans U.T. » en dernier.
 */
export function summarizeBySite(custodies, sites) {
  const bySite = new Map();
  for (const c of custodies || []) {
    const key = c.site_id ?? null;
    if (!bySite.has(key)) bySite.set(key, { site_id: key, out: 0, late: 0 });
    const entry = bySite.get(key);
    entry.out += 1;
    if (c.late) entry.late += 1;
  }
  const nameById = new Map((sites || []).map((s) => [s.id, s.site_name]));
  return Array.from(bySite.values())
    .map((entry) => ({
      ...entry,
      site_name: entry.site_id != null ? (nameById.get(entry.site_id) || null) : null,
    }))
    .sort((a, b) => {
      if (a.site_id == null) return 1;
      if (b.site_id == null) return -1;
      return (a.site_name || '').localeCompare(b.site_name || '');
    });
}

/**
 * La confirmation de clôture d'office.
 *
 * Elle n'est pas une politesse : elle dit QUI détient QUOI, ce que la clôture
 * fait ET ce qu'elle ne prouve pas, exige un motif, et annonce la trace. C'est
 * ce qui sépare une décision opposable d'un clic.
 */
function ForceCloseDialog({ custody, onClose, onConfirm, pending, error, t, fmt }) {
  const [reason, setReason] = useState('');
  const [odometer, setOdometer] = useState('');
  const [immobilize, setImmobilize] = useState(false);
  const [touched, setTouched] = useState(false);
  const missing = touched && !reason.trim();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-200 p-4">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <ShieldOff className="h-5 w-5 text-red-600" />
            {t('poolCustody.forceClose.title')}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label={t('poolCustody.forceClose.cancel')}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 p-4">
          <p className="text-sm text-gray-800">
            {t('poolCustody.forceClose.what', {
              driver: custody.driver_label || t('poolCustody.row.driverUnknown'),
              vehicle: custody.license_plate || custody.vehicle_label || t('poolCustody.row.vehicleUnknown'),
              since: fmt.dateTime(custody.taken_at),
            })}
          </p>
          {/* La conséquence AVANT le bouton, pas après : c'est là qu'elle sert. */}
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {t('poolCustody.forceClose.consequence')}
          </p>

          {error && (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>
          )}

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">{t('poolCustody.forceClose.reason')}</span>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder={t('poolCustody.forceClose.reasonPlaceholder')}
              className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                missing ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-orange-500 focus:ring-orange-500'
              }`}
            />
            {missing && <span className="mt-1 block text-xs text-red-700">{t('poolCustody.errors.reasonRequired')}</span>}
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">{t('poolCustody.forceClose.odometer')}</span>
            <input
              type="number"
              inputMode="numeric"
              value={odometer}
              onChange={(e) => setOdometer(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
            />
            <span className="mt-1 block text-xs text-gray-500">{t('poolCustody.forceClose.odometerHint')}</span>
          </label>

          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={immobilize}
              onChange={(e) => setImmobilize(e.target.checked)}
              className="mt-0.5 h-5 w-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
            <span className="text-sm text-gray-800">
              {t('poolCustody.forceClose.immobilize')}
              <span className="mt-0.5 block text-xs text-gray-500">{t('poolCustody.forceClose.immobilizeHint')}</span>
            </span>
          </label>

          <p className="text-xs text-gray-500">{t('poolCustody.forceClose.traced')}</p>
        </div>

        <div className="flex flex-col gap-2 border-t border-gray-200 p-4 sm:flex-row-reverse">
          <button
            onClick={() => {
              setTouched(true);
              if (!reason.trim()) return;
              onConfirm({
                reason: reason.trim(),
                odometer_km: odometer === '' ? undefined : Number(odometer),
                immobilize,
              });
            }}
            disabled={pending}
            className="min-h-[44px] flex-1 rounded-lg bg-red-600 px-4 py-2.5 font-semibold text-white hover:bg-red-700 disabled:opacity-60"
          >
            {pending ? t('poolCustody.forceClose.sending') : t('poolCustody.forceClose.confirm')}
          </button>
          <button
            onClick={onClose}
            disabled={pending}
            className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-2.5 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            {t('poolCustody.forceClose.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * L'immobilisation d'un véhicule — geste SÉPARÉ de la clôture (le service ne
 * couple jamais les deux d'office).
 *
 * Lot 1.2 (audit CD31 §Q3) : « panne lundi, saisie mardi, fin prévue vendredi
 * — vendredi reste une ESTIMATION ». Le formulaire porte donc trois champs à
 * côté du motif : la cause (vocabulaire fermé), le début EFFECTIF (peut
 * précéder la saisie) et la fin ESTIMÉE (jamais une promesse).
 */
function BlockVehicleDialog({ vehicle, onClose, onConfirm, pending, error, t }) {
  const [reason, setReason] = useState('');
  const [cause, setCause] = useState('');
  const [startedAt, setStartedAt] = useState(() => instantToLocalInput(new Date()));
  const [expectedEndAt, setExpectedEndAt] = useState('');
  const [touched, setTouched] = useState(false);
  const missing = touched && !reason.trim();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-200 p-4">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Ban className="h-5 w-5 text-red-600" />
            {t('poolCustody.block.title')}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label={t('poolCustody.forceClose.cancel')}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 p-4">
          <p className="text-sm text-gray-800">
            {t('poolCustody.block.what', {
              vehicle: vehicle.license_plate || vehicle.vehicle_label || t('poolCustody.row.vehicleUnknown'),
            })}
          </p>

          {error && (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>
          )}

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">{t('poolCustody.block.cause')}</span>
            <select
              value={cause}
              onChange={(e) => setCause(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
            >
              <option value="">{t('poolCustody.block.causeUnset')}</option>
              {BLOCK_CAUSE_CODES.map((code) => (
                <option key={code} value={code}>{t(`poolCustody.block.causes.${code}`)}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">{t('poolCustody.block.startedAt')}</span>
            <input
              type="datetime-local"
              value={startedAt}
              onChange={(e) => setStartedAt(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
            />
            <span className="mt-1 block text-xs text-gray-500">{t('poolCustody.block.startedAtHint')}</span>
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">{t('poolCustody.block.expectedEndAt')}</span>
            <input
              type="datetime-local"
              value={expectedEndAt}
              onChange={(e) => setExpectedEndAt(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
            />
            {/* « Estimation, pas une promesse » — jamais présenté comme une date acquise. */}
            <span className="mt-1 block text-xs text-gray-500">{t('poolCustody.block.expectedEndAtHint')}</span>
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">{t('poolCustody.forceClose.reason')}</span>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder={t('poolCustody.block.reasonPlaceholder')}
              className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                missing ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-orange-500 focus:ring-orange-500'
              }`}
            />
            {missing && <span className="mt-1 block text-xs text-red-700">{t('poolCustody.errors.reasonRequired')}</span>}
          </label>

          <p className="text-xs text-gray-500">{t('poolCustody.forceClose.traced')}</p>
        </div>

        <div className="flex flex-col gap-2 border-t border-gray-200 p-4 sm:flex-row-reverse">
          <button
            onClick={() => {
              setTouched(true);
              if (!reason.trim()) return;
              onConfirm({
                reason: reason.trim(),
                started_at: localInputToInstant(startedAt),
                expected_end_at: expectedEndAt ? localInputToInstant(expectedEndAt) : undefined,
                cause: cause || undefined,
              });
            }}
            disabled={pending}
            className="min-h-[44px] flex-1 rounded-lg bg-red-600 px-4 py-2.5 font-semibold text-white hover:bg-red-700 disabled:opacity-60"
          >
            {pending ? t('poolCustody.forceClose.sending') : t('poolCustody.block.confirm')}
          </button>
          <button
            onClick={onClose}
            disabled={pending}
            className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-2.5 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            {t('poolCustody.forceClose.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

function PoolCustodyPanel({ siteId = null, sites = [], search = '', onChanged, onPickSite }) {
  const { t } = useTranslation();
  const { fmt } = useFormat();
  const queryClient = useQueryClient();
  const [target, setTarget] = useState(null);
  const [blockTarget, setBlockTarget] = useState(null);
  const [error, setError] = useState(null);
  const [blockError, setBlockError] = useState(null);
  const [doneKey, setDoneKey] = useState(null);
  // null = auto (voir plus bas) : tant que l'utilisateur n'a pas cliqué, la
  // section suit les données ; un clic fige son choix pour la session.
  const [onTimeToggle, setOnTimeToggle] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['pool-custody', siteId],
    queryFn: () => poolCustodyAPI.list({ site_id: siteId && siteId !== 'all' ? siteId : undefined, limit: 1000 }),
    refetchInterval: 60_000,
  });

  const payload = data?.data || data || {};
  const custodies = useMemo(() => payload.custodies || [], [payload.custodies]);
  const summary = payload.summary || { out: 0, late: 0, due_within_hour: 0 };

  // Recherche texte : sous 2 caractères, on ne filtre rien (un caractère seul
  // ne discrimine rien et affole le rendu à chaque frappe).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 2) return custodies;
    return custodies.filter((c) => [c.license_plate, c.vehicle_label, c.driver_label]
      .some((v) => v && v.toLowerCase().includes(q)));
  }, [custodies, search]);

  const groups = useMemo(() => groupCustodies(filtered), [filtered]);
  const siteSummaries = useMemo(() => summarizeBySite(filtered, sites), [filtered, sites]);
  const siteNameById = useMemo(() => new Map((sites || []).map((s) => [s.id, s.site_name])), [sites]);

  // Le bandeau n'a de sens que sur « toutes U.T. » et quand il y a
  // effectivement de quoi resserrer — une seule U.T. couverte, il n'ajoute rien.
  const showSiteBar = siteId === 'all' && siteSummaries.length >= 2;
  // Filet de sécurité : si les deux sections prioritaires sont vides, la
  // troisième se déplie d'office pour ne pas laisser l'écran vide.
  const onTimeOpen = onTimeToggle !== null ? onTimeToggle : (groups.late.length === 0 && groups.dueSoon.length === 0);

  const forceClose = useMutation({
    mutationFn: ({ id, body }) => poolCustodyAPI.forceClose(id, body),
    onSuccess: () => {
      setTarget(null);
      setError(null);
      setDoneKey('poolCustody.forceClose.done');
      queryClient.invalidateQueries({ queryKey: ['pool-custody'] });
      onChanged?.();
    },
    onError: (err) => setError(t('poolCustody.errors.actionFailed', { message: err.message })),
  });

  const blockVehicle = useMutation({
    mutationFn: ({ vehicleId, body }) => poolCustodyAPI.block(vehicleId, body),
    onSuccess: () => {
      setBlockTarget(null);
      setBlockError(null);
      setDoneKey('poolCustody.block.done');
      queryClient.invalidateQueries({ queryKey: ['pool-custody'] });
      onChanged?.();
    },
    onError: (err) => setBlockError(t('poolCustody.errors.actionFailed', { message: err.message })),
  });

  if (isLoading) return null;

  /** Une ligne de garde — factorisée : les trois sections rendent la même chose. */
  const renderRow = (c) => (
    <li key={c.reservation_id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="flex items-center gap-2 font-medium text-gray-900">
          <Car className="h-4 w-4 shrink-0 text-gray-400" />
          <span className="truncate">
            {c.license_plate || c.vehicle_label || t('poolCustody.row.vehicleUnknown')}
          </span>
          <span className="text-gray-400">·</span>
          <span className="truncate text-gray-700">{c.driver_label || t('poolCustody.row.driverUnknown')}</span>
        </p>
        <p className="mt-0.5 flex items-center gap-1.5 text-sm text-gray-500">
          <Clock className="h-3.5 w-3.5 shrink-0" />
          {t('poolCustody.row.takenAt', { when: fmt.dateTime(c.taken_at) })}
          {' — '}
          {t('poolCustody.row.dueAt', { when: fmt.time(c.due_at) })}
          {/* Le nom de l'U.T. seulement quand le périmètre les mélange — sinon
              c'est une évidence répétée sur chaque ligne. */}
          {siteId === 'all' && (
            <>
              <span className="text-gray-300">·</span>
              <span className="truncate">{siteNameById.get(c.site_id) || t('poolCustody.sites.unassigned')}</span>
            </>
          )}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-3">
        {c.late ? (
          <span className="flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-sm font-semibold text-red-700">
            <AlertTriangle className="h-3.5 w-3.5" />
            {t('poolCustody.row.late', { duration: formatDuration(c.overdue_min) })}
          </span>
        ) : (
          <span className="text-sm text-gray-500">
            {t('poolCustody.row.remaining', { duration: formatDuration(c.remaining_min) })}
          </span>
        )}
        {c.vehicle_id != null && (
          <button
            onClick={() => { setBlockError(null); setBlockTarget(c); }}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:border-red-300 hover:bg-red-50 hover:text-red-700"
          >
            {t('poolCustody.actions.block')}
          </button>
        )}
        <button
          onClick={() => { setError(null); setTarget(c); }}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:border-red-300 hover:bg-red-50 hover:text-red-700"
        >
          {t('poolCustody.actions.forceClose')}
        </button>
      </div>
    </li>
  );

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="p-4">
        {/* Ligne de synthèse — le résumé d'abord, jamais deux fois le même
            véhicule compté (« en retard » est un sous-ensemble de « dehors »). */}
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
          <span className="font-semibold text-gray-900">{t('poolCustody.summary.out', { count: summary.out })}</span>
          {summary.late > 0 && (
            <>
              <span className="text-gray-300">|</span>
              <span className="font-semibold text-red-600" title={t('poolCustody.summary.lateHint')}>
                {t('poolCustody.summary.late', { count: summary.late })}
              </span>
            </>
          )}
          {summary.due_within_hour > 0 && (
            <>
              <span className="text-gray-300">|</span>
              <span className="font-semibold text-amber-600">
                {t('poolCustody.summary.dueWithinHour', { count: summary.due_within_hour })}
              </span>
            </>
          )}
        </div>

        {doneKey && (
          <p className="mb-3 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            {t(doneKey)}
            <button onClick={() => setDoneKey(null)} className="ml-auto text-emerald-700"><X className="h-4 w-4" /></button>
          </p>
        )}
        {error && !target && (
          <p className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>
        )}

        {/* Bandeau par U.T. : le geste « je resserre sur mon site » d'un clic —
            seulement quand « toutes U.T. » mélange effectivement plusieurs sites. */}
        {showSiteBar && (
          <div className="mb-4 flex flex-wrap gap-2">
            {siteSummaries.map((s) => (
              <button
                key={s.site_id ?? 'none'}
                onClick={() => onPickSite?.(s.site_id)}
                className="flex items-center gap-1.5 rounded-full border border-gray-300 bg-gray-50 px-3 py-1.5 text-sm font-medium text-gray-700 hover:border-orange-300 hover:bg-orange-50"
              >
                <MapPin className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                <span className="truncate">{s.site_name || t('poolCustody.sites.unassigned')}</span>
                <span className="text-gray-400">·</span>
                <span>{t('poolCustody.summary.out', { count: s.out })}</span>
                {s.late > 0 && (
                  <span className="font-semibold text-red-600">{t('poolCustody.summary.late', { count: s.late })}</span>
                )}
              </button>
            ))}
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="py-6 text-center">
            <p className="text-sm font-medium text-gray-700">{t('poolCustody.empty')}</p>
            <p className="mt-0.5 text-xs text-gray-500">{t('poolCustody.emptyHint')}</p>
          </div>
        ) : (
          <div className="space-y-5">
            {groups.late.length > 0 && (
              <section>
                <h4 className="mb-1 text-sm font-semibold text-red-700">
                  {t('poolCustody.sections.late', { count: groups.late.length })}
                </h4>
                <ul className="divide-y divide-gray-100">{groups.late.map(renderRow)}</ul>
              </section>
            )}

            {groups.dueSoon.length > 0 && (
              <section>
                <h4 className="mb-1 text-sm font-semibold text-amber-700">
                  {t('poolCustody.sections.dueSoon', { count: groups.dueSoon.length })}
                </h4>
                <ul className="divide-y divide-gray-100">{groups.dueSoon.map(renderRow)}</ul>
              </section>
            )}

            {groups.onTime.length > 0 && (
              <section>
                <button
                  onClick={() => setOnTimeToggle(!onTimeOpen)}
                  className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-gray-700"
                  aria-expanded={onTimeOpen}
                >
                  {onTimeOpen ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                  {t('poolCustody.sections.onTime', { count: groups.onTime.length })}
                  <span className="font-normal text-gray-400">
                    {onTimeOpen ? t('poolCustody.actions.collapse') : t('poolCustody.actions.expand')}
                  </span>
                </button>
                {onTimeOpen && <ul className="divide-y divide-gray-100">{groups.onTime.map(renderRow)}</ul>}
              </section>
            )}
          </div>
        )}
      </div>

      {target && (
        <ForceCloseDialog
          custody={target}
          pending={forceClose.isPending}
          error={error}
          onClose={() => { setTarget(null); setError(null); }}
          onConfirm={(body) => forceClose.mutate({ id: target.reservation_id, body })}
          t={t}
          fmt={fmt}
        />
      )}

      {blockTarget && (
        <BlockVehicleDialog
          vehicle={blockTarget}
          pending={blockVehicle.isPending}
          error={blockError}
          onClose={() => { setBlockTarget(null); setBlockError(null); }}
          onConfirm={(body) => blockVehicle.mutate({ vehicleId: blockTarget.vehicle_id, body })}
          t={t}
        />
      )}
    </div>
  );
}

export default PoolCustodyPanel;
